import { z } from 'zod';
import { fileCategorySchema } from '../files/categories.js';
import { fileSourceSchema } from '../files/stored-file.js';
import { storePlanSchema } from '../files/store-plan.js';
import { digestSchema } from '../hashing/hash.js';
import { tokenPurposeSchema } from '../portal/tokens.js';
import { credentialDetailsSchema, verificationOutcomeSchema } from '../credentials/evidence-details.js';
/**
 * What approving a step causes.
 *
 * Effects are serialisable DATA, never closures: they cross a queue (PLAN §6 rule 2 —
 * cross-feature work happens through events, not direct calls), so they must survive
 * JSON round-tripping and a process restart.
 */
/**
 * Commit an approved, clean file to protected storage and link it as evidence (#183, ADR-009 §2).
 * Replaces `route-file`.
 *
 * `onApprove` is pure and has no upload in hand, so it emits `plan: null`; the approval path
 * pins the plan (`attachStorePlans`) before the envelope is written, and the handler obeys it.
 */
export const storeFileEffectSchema = z.object({
    kind: z.literal('store-file'),
    instanceId: z.string().min(1),
    uploadId: z.string().min(1),
    /** Closed vocabulary. Segregation is derived from it — never a flag beside it. */
    category: fileCategorySchema,
    /** How the bytes reached staging: a person's upload, or a PDF the server filled and flattened. */
    source: fileSourceSchema,
    plan: storePlanSchema.nullable().default(null),
});
/**
 * Re-commit a misfiled file's bytes to where they belong (#194, ADR-009 §3a step 2).
 *
 * Enqueued by the HR Admin correction command, never by a step: `onApprove` has no way to emit it
 * and the step contract refuses it. The plan is pinned when the correction is recorded — the new
 * FileRecord's id, the target category and its store, and the misfiled record's digest, which the
 * bytes read back from the wrong store must still hash to. `targetPersonId` is whose file it is;
 * the envelope's own `causedBy.personId` is the misfiled record's person, where the correction lives.
 * Ids and a digest only, like every envelope.
 */
export const correctFileEffectSchema = z.object({
    kind: z.literal('correct-file'),
    correctionId: z.string().uuid(),
    misfiledFileId: z.string().uuid(),
    targetPersonId: z.string().min(1),
    plan: storePlanSchema,
});
/**
 * LEGACY-READ (ADR-009, #186). The plan a stored `route-file` envelope carries. The SharePoint
 * router that produced and obeyed it is deleted; the dispatcher's conversion (#183) reads only
 * `columns.ContentHash`, the digest HR approved. So this is deliberately LOOSER than the schema
 * that wrote these envelopes — it asks only for what the conversion reads and passes the rest
 * through — and every envelope that parsed then still parses. The category comes from the effect's
 * `target`, never from here, so nothing in the passthrough can steer where a file is committed.
 */
export const legacyRoutePlanSchema = z
    .object({
    columns: z.object({ ContentHash: digestSchema }).passthrough(),
})
    .passthrough();
/**
 * LEGACY — SharePoint filing (#61), retired by ADR-009. No step type emits it any more; it stays
 * in the union only so the envelopes already stored on retained staging still parse, and the
 * dispatcher converts each one to `store-file` at the handler boundary (#183). Remove it once
 * those have drained.
 */
export const routeFileEffectSchema = z.object({
    kind: z.literal('route-file'),
    instanceId: z.string().min(1),
    uploadId: z.string().min(1),
    /** Closed vocabulary (files/categories.ts); becomes the converted store-file's category. */
    target: fileCategorySchema,
    /** Pinned at approval; null for envelopes written before the router existed. */
    plan: legacyRoutePlanSchema.nullable().default(null),
});
export const mintCredentialEffectSchema = z.object({
    kind: z.literal('mint-credential'),
    personId: z.string().min(1),
    requirementKey: z.string().min(1),
    identifier: z.string().nullable().default(null),
    issuedAt: z.string().datetime().nullable().default(null),
    expiresAt: z.string().datetime().nullable().default(null),
    details: credentialDetailsSchema.optional(),
    evidenceUrl: z.string().url().nullable().optional(),
});
export const recordVerificationEffectSchema = z.object({
    kind: z.literal('record-verification'),
    personId: z.string().min(1),
    requirementKey: z.string().min(1),
    method: z.string().min(1),
    verifiedBy: z.string().min(1),
    occurredAt: z.string().datetime().optional(),
    outcome: verificationOutcomeSchema.optional(),
    source: z.string().max(200).optional(),
    reference: z.string().max(200).optional(),
    note: z.string().max(1000).optional(),
    evidenceUrl: z.string().url().nullable().optional(),
    recordedBy: z.string().min(1).optional(),
});
/**
 * The messages the system can send. Closed, because every one needs a rendered template, a
 * test, and a decision about what it may safely say to someone with no account — an open
 * string would let a caller name a template that does not exist and fail at send time,
 * after the step was already approved.
 */
export const emailTemplateSchema = z.enum([
    'portal-invite',
    'portal-link-reissued',
    'document-request',
    'expiry-reminder',
    'submission-rejected',
    /** The HR composer's free-form message; the body lives on the Communication record. */
    'custom',
]);
export const sendEmailEffectSchema = z.object({
    kind: z.literal('send-email'),
    personId: z.string().min(1),
    template: emailTemplateSchema,
    /** Scoped token purpose, when the message must carry a portal or upload link. */
    /** Closed: an unknown purpose must fail here, not reach the token issuer. */
    tokenPurpose: tokenPurposeSchema.nullable().default(null),
    /**
     * The Communication record carrying the body, for `custom` and for anything HR edited
     * before sending. Null for a template rendered entirely from its own inputs.
     */
    communicationId: z.string().min(1).nullable().default(null),
});
export const recomputeRollupEffectSchema = z.object({
    kind: z.literal('recompute-rollup'),
    personId: z.string().min(1),
});
const effectUnionSchema = z.discriminatedUnion('kind', [
    storeFileEffectSchema,
    correctFileEffectSchema,
    routeFileEffectSchema,
    mintCredentialEffectSchema,
    recordVerificationEffectSchema,
    sendEmailEffectSchema,
    recomputeRollupEffectSchema,
]);
/**
 * The union, plus the one cross-field rule a discriminated union cannot hold on its member (a
 * refined object is no longer a plain object option): a store-file's plan must be for the SAME
 * category the effect names (#183 review). Otherwise a forged envelope could say `segregated-i9`
 * while carrying a valid `employee-records` plan, and commit an I-9 to the records account under
 * an audit row that claims it is segregated. The handler refuses the same mismatch again.
 */
export const effectSchema = effectUnionSchema.superRefine((effect, ctx) => {
    if (effect.kind === 'store-file' && effect.plan !== null && effect.plan.category !== effect.category) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['plan', 'category'], message: 'a store plan must be for the category the effect names' });
    }
});
/** The kinds as a closed enum, derived from the union so it cannot drift from it. */
export const effectKindSchema = z.enum(effectUnionSchema.options.map((option) => option.shape.kind.value));
/**
 * Envelope written to the queue.
 *
 * `idempotencyKey` is derived, not random: a queue may deliver the same message twice,
 * and the handler must be able to recognise the repeat. Deriving it from the cause
 * (instance + attempt + effect position) means a genuine retry carries the same key
 * while a genuinely new effect does not.
 */
export const effectEnvelopeSchema = z.object({
    idempotencyKey: z.string().min(1),
    causedBy: z.object({
        /**
         * The step instance whose decision caused the effect — or, for an HR-composed email that
         * belongs to no step (#62), the id of the Communication it sends (`comm:composer:<operationId>`).
         * Either way it is the stable, per-person cause the idempotency key is derived from; a
         * handler that needs an instance must not assume one exists for a `send-email` of `custom`.
         */
        instanceId: z.string().min(1),
        /**
         * Required, and not redundant with the effects' own ids: `instances` and `audit` are
         * both partitioned on /personId, so without it a handler cannot point-read the
         * instance it is acting on or write its audit row without a cross-partition scan.
         */
        personId: z.string().min(1),
        /** Increments only when the step is re-approved after a rejection. */
        attempt: z.number().int().nonnegative(),
    }),
    index: z.number().int().nonnegative(),
    effect: effectSchema,
});
export function idempotencyKeyFor(instanceId, attempt, index, kind) {
    return `${instanceId}:${attempt}:${index}:${kind}`;
}
export function envelopeEffects(instanceId, personId, attempt, effects) {
    return effects.map((effect, index) => ({
        idempotencyKey: idempotencyKeyFor(instanceId, attempt, index, effect.kind),
        causedBy: { instanceId, personId, attempt },
        index,
        effect,
    }));
}
/**
 * Compile-time exhaustiveness. A handler switch ending in `assertNeverEffect(effect)`
 * stops compiling the moment a new effect kind is added, which is the point: a silently
 * unhandled effect would mean an approved step that never commits its file.
 */
export function assertNeverEffect(effect) {
    throw new Error(`unhandled effect: ${JSON.stringify(effect)}`);
}
//# sourceMappingURL=effects.js.map